/**
 * useNotificationQueries — react-query 기반 알림 데이터 훅.
 *
 * 정책 (2026-05-15, /plan-eng-review 결정):
 *   - refetchInterval 없음 — push invalidate + windowFocus refetch 로 갱신.
 *   - refetchOnWindowFocus: true — 사용자가 앱 다시 켜면 즉시 갱신.
 *   - staleTime: 30_000 — 30초 내 cache hit (탭 전환 시 빠른 응답).
 *
 * Source: docs/13_ACTIVITY_REFACTOR_HANDOFF.md §13 + 2026-05-15 인박스 적재 plan.
 *
 * 키 분리 (2026-06-13):
 *   - 리스트(NOTIFICATIONS_KEY)는 알림센터 진입 시에만 fetch.
 *   - AppBar 뱃지는 모든 화면 상단에 상주 → 유무(dot) 하나를 위해 전체 페이로드를 받지
 *     않고 경량 존재검사 엔드포인트(GET /notifications/unread-count)만 호출.
 *   - 카운트 키는 리스트 키의 prefix child 이므로
 *     invalidateQueries({ queryKey: NOTIFICATIONS_KEY }) 한 번으로 둘 다 무효화된다
 *     (react-query 기본 prefix 매칭). 푸시 수신 시 동기화가 공짜.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchNotifications,
  getUnreadFlag,
  type NotificationItem,
} from "../api/notification";
import { useAuthStore } from "../store/authStore";

export const NOTIFICATIONS_KEY = ["notifications"] as const;

/**
 * 안 읽은 알림 유무 전용 query key — NOTIFICATIONS_KEY 의 prefix child.
 * invalidateQueries({ queryKey: NOTIFICATIONS_KEY }) 시 prefix 매칭으로 함께 무효화된다.
 */
export const NOTIFICATIONS_COUNT_KEY = ["notifications", "unread-count"] as const;

const REFRESH_OPTS = {
  staleTime: 30_000,
  refetchOnWindowFocus: true,
} as const;

export function useNotificationsQuery() {
  const isAuthed = useAuthStore((s) => !!s.accessToken);
  return useQuery<NotificationItem[]>({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () => fetchNotifications(),
    enabled: isAuthed,
    ...REFRESH_OPTS,
  });
}

/**
 * AppBar 뱃지용 — 안 읽은 알림 유무만 내려주는 경량 존재검사 엔드포인트 사용.
 *
 * 읽음 처리 시 각 페이지가 NOTIFICATIONS_COUNT_KEY 를 optimistic 하게 갱신하고,
 * 푸시/에러 시 prefix invalidate 로 서버 값과 재동기화한다.
 */
export function useHasUnreadNotifications(): boolean {
  const isAuthed = useAuthStore((s) => !!s.accessToken);
  const q = useQuery<boolean>({
    queryKey: NOTIFICATIONS_COUNT_KEY,
    queryFn: () => getUnreadFlag(),
    enabled: isAuthed,
    ...REFRESH_OPTS,
  });
  return useMemo(() => q.data ?? false, [q.data]);
}
